import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import BrandMark from '../components/BrandMark';
import LangSwitch from '../components/LangSwitch';
import { api, API } from '../lib/api';
import { useLicense } from '../lib/license';
import { useT } from '../lib/i18n';

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 8;

export default function Welcome() {
  const { t } = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useLicense();
  const sessionId = params.get('session_id');

  const [phase, setPhase] = useState('checking');
  const [licenseCode, setLicenseCode] = useState('');
  const [isGift, setIsGift] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [downloading, setDownloading] = useState(false);
  const attemptsRef = useRef(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setPhase('failed');
      return;
    }
    cancelledRef.current = false;

    const poll = async () => {
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (cancelledRef.current) return;
        const ps = r.data.payment_status;
        const st = r.data.status;
        if (ps === 'paid') {
          setLicenseCode(r.data.license_code || '');
          setIsGift(!!r.data.is_gift);
          setRecipientEmail(r.data.gift_recipient_email || '');
          setPhase('paid');
          refresh();
          return;
        }
        if (st === 'expired') {
          setPhase('expired');
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
          setPhase('failed');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (_) {
        attemptsRef.current += 1;
        if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
          setPhase('failed');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => { cancelledRef.current = true; };
  }, [sessionId, refresh]);

  const downloadBook = async () => {
    setDownloading(true);
    try {
      const deviceId = localStorage.getItem('ctl_device_id') || '';
      const res = await fetch(`${API}/book`, { headers: { 'X-Device-Id': deviceId } });
      if (!res.ok) throw new Error(`book fetch failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Connection-is-Love.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Welcome.downloadBook failed:', e);
    } finally { setDownloading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col fade-in font-pressura" data-testid="welcome-screen">
      <header className="w-full px-4 sm:px-10 lg:px-16 pt-6 sm:pt-10 lg:pt-12 pb-4 flex items-center justify-center">
        <div aria-hidden="true" className="h-1" />
      </header>

      <main className="flex-1 flex flex-col items-center pt-2 pb-24 px-4">
        <Link to="/" className="block hover:opacity-90 transition-opacity duration-300" aria-label="Connect to Love — home">
          <BrandMark size="hero" />
        </Link>

        <div className="w-full max-w-xl text-center mt-12 sm:mt-16">
          {phase === 'checking' && (
            <div data-testid="welcome-checking">
              <p className="font-pressura font-bold text-2xl md:text-3xl text-[#000000] mb-4 uppercase tracking-[0.04em]">{t('welcome.confirming')}</p>
              <p className="font-pressura slow-pulse text-sm opacity-70">{t('welcome.confirmingSub')}</p>
            </div>
          )}

          {phase === 'paid' && !isGift && (
            <div className="space-y-10 fade-in" data-testid="welcome-paid">
              <p className="font-pressura font-bold text-2xl md:text-3xl text-[#000000] leading-snug whitespace-pre-line uppercase tracking-[0.04em]">
                {t('welcome.headline')}
              </p>
              <div className="space-y-3">
                <p className="font-pressura text-xs uppercase tracking-[0.18em] text-[#000000] opacity-55 font-bold">
                  {t('welcome.licenseLabel')}
                </p>
                <p
                  className="font-pressura text-2xl md:text-3xl tracking-[0.18em] text-[#000000] font-bold select-all"
                  data-testid="welcome-license-code"
                >
                  {licenseCode || '—'}
                </p>
                <p className="font-pressura text-xs opacity-70 max-w-sm mx-auto leading-relaxed">
                  {t('welcome.licenseHelp')}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-6 items-center justify-center">
                <button
                  className="ctl-btn font-pressura font-bold"
                  onClick={downloadBook}
                  disabled={downloading}
                  data-testid="welcome-download-btn"
                >
                  {downloading ? t('welcome.downloading') : t('welcome.download')}
                </button>
                <button
                  className="ctl-shimmer-copper bg-transparent border-0 p-0 cursor-pointer font-pressura font-bold tracking-[0.18em] uppercase hover:opacity-80 transition-opacity duration-300"
                  style={{ fontSize: '1.0625rem', textDecoration: 'underline', textUnderlineOffset: '0.4em', textDecorationThickness: '2px' }}
                  onClick={() => navigate('/')}
                  data-testid="welcome-enter-btn"
                >
                  {t('welcome.enter')}
                </button>
              </div>
            </div>
          )}

          {phase === 'paid' && isGift && (
            <div className="space-y-8 fade-in" data-testid="welcome-paid-gift">
              <p className="font-pressura font-bold text-2xl md:text-3xl text-[#000000] leading-snug uppercase tracking-[0.04em]">
                {t('welcome.giftSent', '').replace('{email}', recipientEmail)}
              </p>
              <p className="font-pressura text-sm max-w-md mx-auto opacity-75 leading-relaxed">
                {t('welcome.giftEnter')}
              </p>
            </div>
          )}

          {phase === 'expired' && (
            <div data-testid="welcome-expired">
              <p className="font-pressura font-bold text-2xl md:text-3xl text-[#000000] mb-6 uppercase tracking-[0.04em]">{t('welcome.expired')}</p>
              <Link to="/buy" className="ctl-btn font-pressura font-bold inline-flex" data-testid="welcome-retry-link">
                {t('welcome.tryAgain')}
              </Link>
            </div>
          )}

          {phase === 'failed' && (
            <div data-testid="welcome-failed">
              <p className="font-pressura font-bold text-2xl md:text-3xl text-[#000000] mb-6 uppercase tracking-[0.04em]">{t('welcome.failed')}</p>
              <p className="font-pressura text-sm opacity-75 mb-10 leading-relaxed">{t('welcome.failedHelp')}</p>
              <div className="flex gap-6 justify-center">
                <Link to="/redeem" className="ctl-btn font-pressura font-bold inline-flex" data-testid="welcome-redeem-link">
                  {t('welcome.haveLicense')}
                </Link>
                <Link to="/buy" className="ctl-btn-quiet font-pressura inline-flex">
                  {t('welcome.tryAgain')}
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="w-full pb-10 flex items-center justify-center" data-testid="welcome-footer">
        <LangSwitch />
      </footer>
    </div>
  );
}
